// Season Road — the seasonal progression board. Two parallel tracks:
//   • Premium: requires unlocking via levels-completed milestones
//   • Free: rewards earned by everyone as they level up
//
// Players see all milestones from level 1 onward, with a "YOU ARE HERE"
// indicator on the next-to-unlock milestone. Reward catalog is static
// for now — `REWARDS` below — and can be moved into data/ later.

import { useNavigate } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { SEED_LESSONS } from "../data/seed-lessons";
import { isLessonFinished } from "../local/profile";

interface Reward {
  level: number;
  name: string;
  kind: string;
  art: string;
  rarity?: "common" | "rare" | "epic" | "legendary";
}

// Single season-road track — no premium/free split, just a clean road
// of rewards earned as the player completes levels.
const REWARDS: Reward[] = [
  { level: 1,  name: "Runner Cap",     kind: "Hat",            art: "🧢" },
  { level: 3,  name: "50 Coins",       kind: "Coins",          art: "🪙" },
  { level: 5,  name: "Pink Runner",    kind: "Character Color", art: "🎀" },
  { level: 8,  name: "100 XP",         kind: "XP",             art: "✨" },
  { level: 10, name: "Golden Crown",   kind: "Hat",            art: "👑" },
  { level: 15, name: "Star Shades",    kind: "Face Accessory", art: "🕶", rarity: "epic" },
  { level: 20, name: "Galaxy Suit",    kind: "Costume",        art: "👽" },
  { level: 25, name: "500 Gems",       kind: "Gems",           art: "💎" },
  { level: 30, name: "Champion Badge", kind: "Badge",          art: "🏅", rarity: "legendary" },
  { level: 40, name: "Surprise Box",   kind: "Mystery",        art: "🎁" },
  { level: 50, name: "Halo",           kind: "Hat",            art: "😇" },
  { level: 60, name: "Heart Cheer",    kind: "Emote",          art: "💗" },
  { level: 70, name: "Star Trail",     kind: "Trail",          art: "🌟" },
  { level: 80, name: "Champion Cup",   kind: "Trophy",         art: "🏆", rarity: "legendary" },
];

export function SeasonRoad() {
  const nav = useNavigate();
  const { profile } = useLocalProfile();

  if (!profile) {
    nav("/", { replace: true });
    return null;
  }

  const levelsCompleted = SEED_LESSONS.filter((l) => isLessonFinished(profile, l.id)).length;
  const totalLevels = 80;
  // "Current" milestone: the first un-unlocked reward.
  const currentRewardIdx = REWARDS.findIndex((r) => levelsCompleted < r.level);
  const currentReward = (currentRewardIdx >= 0 ? REWARDS[currentRewardIdx] : REWARDS[REWARDS.length - 1])!;

  const claimAndPlay = () => nav("/play");

  return (
    <div className="season-road-page">
      <header className="season-road-top">
        <button className="season-road-back" onClick={() => nav("/")} aria-label="Back">←</button>
        <h1 className="season-road-title">
          <span className="wr">WORD RUNNER</span>
          <span className="sr"><span aria-hidden>⭐</span> SEASON ROAD <span aria-hidden>⭐</span></span>
        </h1>
        <div className="season-road-progress">
          <span aria-hidden style={{ fontSize: 22 }}>⭐</span>
          <div style={{ flex: 1 }}>
            <strong><em>{levelsCompleted}</em> / {totalLevels}</strong>
            <small>levels completed</small>
            <div className="season-road-progress-bar">
              <i style={{ width: `${Math.max(2, (levelsCompleted / totalLevels) * 100)}%` }} />
            </div>
          </div>
        </div>
      </header>

      <p className="season-road-tagline">⭐ Play, learn and unlock awesome rewards!</p>

      {/* Single swipeable season road */}
      <div className="season-road-board">
        <div className="season-road-swipe">
          {REWARDS.map((r) => {
            const unlocked = levelsCompleted >= r.level;
            const isCurrent = r.level === currentReward.level;
            return (
              <div
                key={r.level}
                className={`season-road-node ${unlocked ? "completed" : "locked"} ${isCurrent ? "current" : ""}`}
              >
                {isCurrent && <span className="season-road-youhere">YOU ARE HERE</span>}
                <span className="season-road-node-level">{r.level}</span>
                <span className="season-road-node-art" aria-hidden>{r.art}</span>
                <strong className="season-road-node-name">{r.name}</strong>
                <small className="season-road-node-kind">{r.kind}</small>
                {unlocked ? (
                  <span className="season-road-node-status">✓</span>
                ) : (
                  <span className="season-road-node-locktext"><span aria-hidden>🔒</span> Unlock at Lv {r.level}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Featured detail card */}
      <div className="season-road-detail">
        <div className="season-road-detail-art" aria-hidden>{currentReward.art}</div>
        <div className="season-road-detail-meta">
          <strong>{currentReward.name} {currentReward.rarity && <span className="badge">{currentReward.rarity.toUpperCase()}</span>}</strong>
          <small>{currentReward.kind}</small>
          <p>Shine bright and stand out! Show off your star power.</p>
        </div>
        <div className="season-road-detail-unlocks">
          <small>Unlocks at</small>
          <strong>Level {currentReward.level}</strong>
          {levelsCompleted >= currentReward.level ? (
            <em style={{ color: "#15b67a", fontWeight: 900 }}>✓ You are here!</em>
          ) : (
            <em>{currentReward.level - levelsCompleted} more to go</em>
          )}
        </div>
        <div className="season-road-detail-end">
          <small>Season Ends In</small>
          <strong>23d 14h 32m</strong>
          <em>Keep playing to unlock amazing rewards!</em>
        </div>
      </div>

      <div className="season-road-actions">
        <button className="picker2-flashcards" onClick={() => nav("/")}>
          <span aria-hidden>←</span> Back to Menu
        </button>
        <button className="picker2-play" onClick={claimAndPlay}>
          <span aria-hidden>🎁</span> Claim Reward
        </button>
        <button className="picker2-random" onClick={() => nav("/play")}>
          <span aria-hidden>≡</span> See All Rewards
        </button>
      </div>
    </div>
  );
}
